import { useState } from 'react'

import { StockMovementChart } from '@/components/charts/stock-movement-chart'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useDefects, useMovementByTime } from '@/hooks/use-report-query'
import { m } from '@/lib/paraglide/messages'

type GroupBy = 'day' | 'week' | 'month' | 'year'

const GROUP_OPTIONS: { value: GroupBy; label: () => string }[] = [
    { value: 'day', label: m.report_group_day },
    { value: 'week', label: m.report_group_week },
    { value: 'month', label: m.report_group_month },
    { value: 'year', label: m.report_group_year },
]

/**
 * Warehouse time-series reports: stock movement chart + defective-product
 * table sharing one date range and a day/week/month/year granularity toggle.
 */
export function WarehouseMovementSection() {
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const [groupBy, setGroupBy] = useState<GroupBy>('day')

    const params = {
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        group_by: groupBy,
    }
    const { data: movement = [] } = useMovementByTime(params)
    const { data: defects = [] } = useDefects(params)

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                    <Label className="text-xs">
                        {m.cashflow_filter_from()}
                    </Label>
                    <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="w-40"
                    />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">{m.cashflow_filter_to()}</Label>
                    <Input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="w-40"
                    />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">{m.report_group_by()}</Label>
                    <div className="flex gap-1">
                        {GROUP_OPTIONS.map((opt) => (
                            <Button
                                key={opt.value}
                                type="button"
                                size="sm"
                                variant={
                                    groupBy === opt.value
                                        ? 'default'
                                        : 'outline'
                                }
                                onClick={() => setGroupBy(opt.value)}
                            >
                                {opt.label()}
                            </Button>
                        ))}
                    </div>
                </div>
            </div>

            <StockMovementChart data={movement} />

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">
                        {m.warehouse_defects_title()}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>
                                    {m.warehouse_defects_period()}
                                </TableHead>
                                <TableHead>
                                    {m.warehouse_defects_product()}
                                </TableHead>
                                <TableHead className="text-right">
                                    {m.warehouse_defects_quantity()}
                                </TableHead>
                                <TableHead className="text-right">
                                    {m.warehouse_defects_notes()}
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {defects.length === 0 && (
                                <TableRow>
                                    <TableCell
                                        colSpan={4}
                                        className="text-center text-muted-foreground py-4"
                                    >
                                        {m.warehouse_defects_empty()}
                                    </TableCell>
                                </TableRow>
                            )}
                            {defects.map((d) => (
                                <TableRow key={`${d.period}-${d.product_id}`}>
                                    <TableCell>{d.period}</TableCell>
                                    <TableCell>{d.product_name}</TableCell>
                                    <TableCell className="text-right font-mono">
                                        {d.quantity}
                                    </TableCell>
                                    <TableCell className="text-right font-mono">
                                        {d.note_count}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    )
}
